import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import type {
  Agency, Assignment, Department, Employee, Shift, ShiftCode, TimeOff,
} from "../lib/types";
import { effectiveHomeShiftFor } from "../lib/tacMode";
import { parseHourNote, hourOf } from "../lib/hourBuckets";
import { Button } from "../components/ui/Button";
import { can } from "../lib/permissions";
import { useEffectiveUser } from "../lib/auth";

// Records page — running working-hours count per employee across
// the current pay period, grouped by department and (lightly) by
// shift. L2+ can click any cell to override the schedule-derived
// value for payroll tracking, and the four trailing columns V /
// C / S / PL hold pay-period totals of vacation, comp, sick, and
// personal leave hours. BETA — verify against timekeeping before
// payroll use.

type LeaveBucket = "v" | "c" | "s" | "pl";
const LEAVE_BUCKETS: LeaveBucket[] = ["v", "c", "s", "pl"];
const LEAVE_LABELS: Record<LeaveBucket, string> = {
  v: "V",
  c: "C",
  s: "S",
  pl: "PL",
};
const LEAVE_TITLES: Record<LeaveBucket, string> = {
  v: "Vacation hours (period total)",
  c: "Comp-time hours (period total)",
  s: "Sick hours (period total)",
  pl: "Personal leave hours (period total)",
};

export function Records() {
  const me = useEffectiveUser();
  const mayEdit = can.editRoster(me);
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const PERIOD_DAYS = 14;

  function sundayOf(d: Date): Date {
    const c = new Date(d); c.setHours(0, 0, 0, 0);
    c.setDate(c.getDate() - c.getDay());
    return c;
  }
  function todaySunday(): Date {
    return sundayOf(new Date());
  }

  const [anchor, setAnchor] = useState<Date>(() => todaySunday());
  const days = useMemo(
    () => Array.from({ length: PERIOD_DAYS }, (_, i) => addDays(anchor, i)),
    [anchor],
  );
  const startStr = ymd(days[0]);
  const endStr = ymd(days[days.length - 1]);

  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const shiftCodes = useQuery({ queryKey: ["shiftCodes"], queryFn: api.listShiftCodes });
  const assignments = useQuery({
    queryKey: ["assignments", startStr, endStr],
    queryFn: () => api.listAssignments(startStr, endStr),
  });
  const timeOff = useQuery({
    queryKey: ["timeoff", startStr, endStr],
    queryFn: () => api.listTimeOff(startStr, endStr),
  });
  // Override rows cover the day cells (bucket='work', date = the
  // actual date) AND the leave-column totals (bucket in v/c/s/pl,
  // date = the pay-period Sunday). One query across the whole span
  // keeps the fetch cheap; the index-by-key below pulls what each
  // row needs.
  const overrides = useQuery({
    queryKey: ["recordsOverrides", startStr, endStr],
    queryFn: () => api.listRecordsOverrides(startStr, endStr),
  });

  const shiftById = useMemo(
    () => new Map((shifts.data ?? []).map((s) => [s.id, s])),
    [shifts.data],
  );
  const codeMap = useMemo(
    () => new Map((shiftCodes.data ?? []).map((c) => [c.code.toUpperCase(), c])),
    [shiftCodes.data],
  );

  const assignmentsByKey = useMemo(() => {
    const m = new Map<string, Assignment[]>();
    for (const a of assignments.data ?? []) {
      if (a.status === "cancelled") continue;
      const k = `${a.employeeId}|${a.date}`;
      const list = m.get(k) ?? [];
      list.push(a);
      m.set(k, list);
    }
    return m;
  }, [assignments.data]);
  const otGroupSize = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of assignments.data ?? []) {
      if (a.status === "cancelled") continue;
      if (!a.coverageGroupId) continue;
      const n = (a.note ?? "").trim().toUpperCase();
      if (n !== "OT" && n !== "OT½" && n !== "OT/2") continue;
      m.set(a.coverageGroupId, (m.get(a.coverageGroupId) ?? 0) + 1);
    }
    return m;
  }, [assignments.data]);
  const timeOffByKey = useMemo(() => {
    const m = new Map<string, TimeOff[]>();
    for (const t of timeOff.data ?? []) {
      if (t.status !== "approved") continue;
      const start = new Date(t.startDate + "T00:00:00");
      const end = new Date(t.endDate + "T00:00:00");
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const k = `${t.employeeId}|${ymd(d)}`;
        const list = m.get(k) ?? [];
        list.push(t);
        m.set(k, list);
      }
    }
    return m;
  }, [timeOff.data]);
  // (employeeId|date|bucket) → hours
  const overrideByKey = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of overrides.data ?? []) {
      m.set(`${o.employeeId}|${o.date}|${o.bucket}`, o.hours);
    }
    return m;
  }, [overrides.data]);

  // Group active officers by (department → home shift). Still
  // bucket homeless officers into an "Other" synthetic dept at
  // the end so they don't vanish from the pay sheet.
  const roster = useMemo(() => {
    const perShift = new Map<string, Employee[]>();
    const perDeptUnassigned = new Map<string, Employee[]>();
    const homeless: Employee[] = [];
    for (const e of employees.data ?? []) {
      if (e.status === "inactive" || e.deletedAt) continue;
      if (e.homeShiftId) {
        const list = perShift.get(e.homeShiftId) ?? [];
        list.push(e); perShift.set(e.homeShiftId, list);
        continue;
      }
      if (e.departmentId) {
        const list = perDeptUnassigned.get(e.departmentId) ?? [];
        list.push(e); perDeptUnassigned.set(e.departmentId, list);
        continue;
      }
      homeless.push(e);
    }
    const cmpEmp = (a: Employee, b: Employee) =>
      a.lastName.localeCompare(b.lastName)
      || a.firstName.localeCompare(b.firstName);
    perShift.forEach((v) => v.sort(cmpEmp));
    perDeptUnassigned.forEach((v) => v.sort(cmpEmp));
    homeless.sort(cmpEmp);
    return { perShift, perDeptUnassigned, homeless };
  }, [employees.data]);

  const sortedDepts = useMemo(
    () => [...(departments.data ?? [])].sort(
      (a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
        || a.name.localeCompare(b.name),
    ),
    [departments.data],
  );

  function shiftsForDept(deptId: string): Shift[] {
    return (shifts.data ?? [])
      .filter((s) => s.departmentId === deptId)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }

  const isLoading = agency.isLoading || departments.isLoading
    || employees.isLoading || shifts.isLoading || shiftCodes.isLoading
    || assignments.isLoading || timeOff.isLoading || overrides.isLoading;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Records</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Running working hours per officer for a two-week pay
            period (Sun → Sat), grouped by department and shift.
            {mayEdit && " Click any cell to override the computed value for payroll."}
            {" Beta — verify against timekeeping before payroll use."}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-1">
              Pay-period start (Sunday)
            </label>
            <input
              type="date"
              value={startStr}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const [y, m, dd] = v.split("-").map(Number);
                if (!y || !m || !dd) return;
                const picked = new Date(y, m - 1, dd);
                setAnchor(sundayOf(picked));
              }}
              className="rounded border-slate-300 text-sm px-2 py-1"
              title="Pick any date — snaps to the Sunday of that week"
            />
          </div>
          <Button
            variant="secondary" size="sm"
            onClick={() => setAnchor(addDays(anchor, -PERIOD_DAYS))}
            title="Previous 2-week pay period"
          >
            ← Previous
          </Button>
          <Button
            variant="secondary" size="sm"
            onClick={() => setAnchor(todaySunday())}
            title="Jump to the pay period containing today"
          >
            Today
          </Button>
          <Button
            variant="secondary" size="sm"
            onClick={() => setAnchor(addDays(anchor, PERIOD_DAYS))}
            title="Next 2-week pay period"
          >
            Next →
          </Button>
        </div>
      </header>

      <div className="text-sm text-slate-600 dark:text-slate-400 font-mono">
        {startStr} → {endStr}
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-6">
          {sortedDepts.map((dept) => {
            const deptShifts = shiftsForDept(dept.id);
            const unassigned = roster.perDeptUnassigned.get(dept.id) ?? [];
            const totalCount = deptShifts.reduce(
              (sum, s) => sum + (roster.perShift.get(s.id)?.length ?? 0),
              0,
            ) + unassigned.length;
            if (totalCount === 0) return null;
            return (
              <DeptSection
                key={dept.id}
                dept={dept}
                deptShifts={deptShifts}
                perShift={roster.perShift}
                unassigned={unassigned}
                days={days}
                periodStart={startStr}
                agency={agency.data ?? null}
                assignmentsByKey={assignmentsByKey}
                otGroupSize={otGroupSize}
                timeOffByKey={timeOffByKey}
                shiftById={shiftById}
                codeMap={codeMap}
                overrideByKey={overrideByKey}
                mayEdit={mayEdit}
              />
            );
          })}
          {roster.homeless.length > 0 && (
            <DeptSection
              dept={{
                id: "__homeless__",
                name: "Other",
                sortOrder: 999,
              } as Department}
              deptShifts={[]}
              perShift={roster.perShift}
              unassigned={roster.homeless}
              days={days}
              periodStart={startStr}
              agency={agency.data ?? null}
              assignmentsByKey={assignmentsByKey}
              otGroupSize={otGroupSize}
              timeOffByKey={timeOffByKey}
              shiftById={shiftById}
              codeMap={codeMap}
              overrideByKey={overrideByKey}
              mayEdit={mayEdit}
            />
          )}
        </div>
      )}
    </div>
  );
}

function DeptSection({
  dept, deptShifts, perShift, unassigned, days, periodStart, agency,
  assignmentsByKey, otGroupSize, timeOffByKey, shiftById, codeMap,
  overrideByKey, mayEdit,
}: {
  dept: Department;
  deptShifts: Shift[];
  perShift: Map<string, Employee[]>;
  unassigned: Employee[];
  days: Date[];
  periodStart: string;
  agency: Agency | null;
  assignmentsByKey: Map<string, Assignment[]>;
  otGroupSize: Map<string, number>;
  timeOffByKey: Map<string, TimeOff[]>;
  shiftById: Map<string, Shift>;
  codeMap: Map<string, ShiftCode>;
  overrideByKey: Map<string, number>;
  mayEdit: boolean;
}) {
  // Build the row list grouped by shift. A shift block becomes a
  // single [header, …rows] run; the Unassigned block (officers in
  // the dept with no home shift) tails every dept that has any.
  const blocks: Array<{
    key: string;
    label: string;
    sub?: string;
    color?: string;
    officers: Employee[];
  }> = [];
  for (const s of deptShifts) {
    const list = (perShift.get(s.id) ?? []).slice()
      .sort((a, b) =>
        a.lastName.localeCompare(b.lastName)
        || a.firstName.localeCompare(b.firstName),
      );
    if (list.length === 0) continue;
    blocks.push({
      key: s.id,
      label: s.name,
      sub: `${s.startTime} • ${Math.round(s.durationMin / 60)}h`,
      color: s.color,
      officers: list,
    });
  }
  if (unassigned.length > 0) {
    blocks.push({
      key: "__unassigned__",
      label: "Unassigned",
      officers: unassigned.slice().sort((a, b) =>
        a.lastName.localeCompare(b.lastName)
        || a.firstName.localeCompare(b.firstName),
      ),
    });
  }

  const colCount = 1 /* officer */ + days.length + 1 /* total */ + LEAVE_BUCKETS.length;

  return (
    <div className="flex justify-center">
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden inline-block">
        <header className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">{dept.name}</h2>
        </header>
        <div className="overflow-x-auto">
          {/* table-layout: fixed pins the column widths to the
            * <colgroup> below, so swapping a cell's display text
            * for the inline <input> on click can't widen the
            * column (and ripple through every neighbor). Without
            * this, the browser re-measures content on every
            * render and the table "jumps" the moment you start
            * typing. */}
          <table className="text-xs" style={{ width: "auto", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 180 }} />
              {days.map((d) => (
                <col key={d.toISOString()} style={{ width: 56 }} />
              ))}
              <col style={{ width: 64 }} />
              {LEAVE_BUCKETS.map((b) => (
                <col key={b} style={{ width: 56 }} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                <th className="text-left px-3 py-2 font-medium sticky left-0 bg-white dark:bg-slate-900">
                  Officer
                </th>
                {days.map((d) => (
                  <th
                    key={d.toISOString()}
                    className={
                      "text-center px-0 py-2 font-medium tabular-nums "
                      + (d.getDay() === 0 || d.getDay() === 6
                        ? "bg-slate-50 dark:bg-slate-800/60 " : "")
                    }
                    title={ymd(d)}
                  >
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      {d.toLocaleDateString(undefined, { weekday: "narrow" })}
                    </div>
                    <div>{d.getDate()}</div>
                  </th>
                ))}
                <th className="text-right px-2 py-2 font-semibold text-slate-700 dark:text-slate-200 border-l border-slate-200 dark:border-slate-700">
                  Total
                </th>
                {LEAVE_BUCKETS.map((b) => (
                  <th
                    key={b}
                    className="text-center px-2 py-2 font-semibold text-slate-700 dark:text-slate-200 border-l border-slate-200 dark:border-slate-700"
                    title={LEAVE_TITLES[b]}
                  >
                    {LEAVE_LABELS[b]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blocks.map((block, bi) => (
                <ShiftBlock
                  key={block.key}
                  block={block}
                  firstBlock={bi === 0}
                  colCount={colCount}
                  days={days}
                  periodStart={periodStart}
                  agency={agency}
                  assignmentsByKey={assignmentsByKey}
                  otGroupSize={otGroupSize}
                  timeOffByKey={timeOffByKey}
                  shiftById={shiftById}
                  codeMap={codeMap}
                  overrideByKey={overrideByKey}
                  mayEdit={mayEdit}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ShiftBlock({
  block, firstBlock, colCount, days, periodStart, agency,
  assignmentsByKey, otGroupSize, timeOffByKey, shiftById, codeMap,
  overrideByKey, mayEdit,
}: {
  block: { key: string; label: string; sub?: string; color?: string; officers: Employee[] };
  firstBlock: boolean;
  colCount: number;
  days: Date[];
  periodStart: string;
  agency: Agency | null;
  assignmentsByKey: Map<string, Assignment[]>;
  otGroupSize: Map<string, number>;
  timeOffByKey: Map<string, TimeOff[]>;
  shiftById: Map<string, Shift>;
  codeMap: Map<string, ShiftCode>;
  overrideByKey: Map<string, number>;
  mayEdit: boolean;
}) {
  // Subtle shift subheader — a thin colored stripe plus the shift
  // name on the far left. The first block skips the spacer row so
  // the shift stripe sits directly under the column header.
  // Only roll up schedule-derived hours for days that are at or
  // before today — future days haven't been worked yet, so showing
  // their scheduled 10h on a payroll sheet is misleading. Admin
  // overrides still win (someone could pre-stamp a day), but the
  // computed fallback is suppressed with `0` past today. Any later
  // schedule change still flows through because the computed value
  // is read live off the same React Query data the schedule uses.
  const todayISO = ymd(new Date());
  const rows = block.officers.map((emp) => {
    const perDay = days.map((d) => {
      const date = ymd(d);
      const key = `${emp.id}|${date}|work`;
      const override = overrideByKey.get(key);
      const isFuture = date > todayISO;
      const computed = isFuture
        ? 0
        : computeDailyHours({
            employee: emp, date, agency, assignmentsByKey, otGroupSize,
            timeOffByKey, shiftById, codeMap,
          });
      return { date, computed, override, isFuture };
    });
    const total = perDay.reduce(
      (sum, cell) => sum + (cell.override ?? cell.computed), 0,
    );
    return { emp, perDay, total };
  });

  return (
    <>
      {!firstBlock && (
        <tr>
          <td colSpan={colCount} className="h-2 p-0 bg-slate-50 dark:bg-slate-800/40 border-y border-slate-100 dark:border-slate-800" />
        </tr>
      )}
      <tr className="bg-slate-50/60 dark:bg-slate-800/40">
        <td
          colSpan={colCount}
          className="px-3 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700"
        >
          <span
            className="inline-block w-2 h-2 rounded-full align-middle mr-2"
            style={{ backgroundColor: block.color ?? "#94a3b8" }}
          />
          {block.label}
          {block.sub && (
            <span className="ml-2 font-normal normal-case tracking-normal text-slate-400 dark:text-slate-500">
              · {block.sub}
            </span>
          )}
          <span className="ml-2 font-normal normal-case tracking-normal text-slate-400 dark:text-slate-500">
            ({block.officers.length})
          </span>
        </td>
      </tr>
      {rows.map(({ emp, perDay, total }) => (
        <OfficerRow
          key={emp.id}
          emp={emp}
          perDay={perDay}
          total={total}
          days={days}
          periodStart={periodStart}
          overrideByKey={overrideByKey}
          mayEdit={mayEdit}
        />
      ))}
    </>
  );
}

function OfficerRow({
  emp, perDay, total, days, periodStart, overrideByKey, mayEdit,
}: {
  emp: Employee;
  perDay: Array<{
    date: string;
    computed: number;
    override: number | undefined;
    isFuture: boolean;
  }>;
  total: number;
  days: Date[];
  periodStart: string;
  overrideByKey: Map<string, number>;
  mayEdit: boolean;
}) {
  return (
    <tr className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-3 py-3 h-11 sticky left-0 bg-white dark:bg-slate-900 truncate text-slate-800 dark:text-slate-100 font-medium">
        {emp.lastName}, {emp.firstName}
      </td>
      {perDay.map((cell, i) => (
        <HoursCell
          key={cell.date}
          employeeId={emp.id}
          date={cell.date}
          bucket="work"
          computed={cell.computed}
          override={cell.override}
          mayEdit={mayEdit}
          isWeekend={days[i].getDay() === 0 || days[i].getDay() === 6}
        />
      ))}
      <td className="text-right px-2 h-11 font-semibold tabular-nums text-slate-900 dark:text-slate-100 border-l border-slate-200 dark:border-slate-700">
        {formatHours(total)}
      </td>
      {LEAVE_BUCKETS.map((b) => {
        const key = `${emp.id}|${periodStart}|${b}`;
        const override = overrideByKey.get(key);
        return (
          <HoursCell
            key={b}
            employeeId={emp.id}
            date={periodStart}
            bucket={b}
            computed={0}
            override={override}
            mayEdit={mayEdit}
            border
          />
        );
      })}
    </tr>
  );
}

// Editable hours cell. Shows the override if set (bold, same ink
// as a plain value) or the computed value (regular weight) and
// opens a native number input in place on click. On blur / Enter
// it either upserts the override (value differs from computed and
// is a valid non-negative number) or deletes the override (user
// cleared the input, or matched the computed value exactly), so
// clearing a cell gracefully falls back to the schedule-derived
// value.
function HoursCell({
  employeeId, date, bucket, computed, override, mayEdit, isWeekend, border,
}: {
  employeeId: string;
  date: string;
  bucket: "work" | LeaveBucket;
  computed: number;
  override: number | undefined;
  mayEdit: boolean;
  isWeekend?: boolean;
  border?: boolean;
}) {
  const qc = useQueryClient();
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string>("");
  // Tracks whether the supervisor actually typed something during
  // this focus session. Blurring without touching the draft is
  // treated as a cancel (no mutation), which lets us safely clear
  // the field on focus — the user can tap away without losing an
  // existing override just because the field was briefly empty.
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const upsertMut = useMutation({
    mutationFn: (hours: number) =>
      api.upsertRecordsOverride(employeeId, date, bucket, hours),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recordsOverrides"] });
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => api.deleteRecordsOverride(employeeId, date, bucket),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recordsOverrides"] });
    },
  });

  const effective = override ?? computed;
  const isOverride = override !== undefined;

  // Mirror the committed value into the draft whenever the cell is
  // NOT being edited, so the input shows the current number even
  // after an upsert / delete settles.
  useEffect(() => {
    if (!focused) {
      setDraft(effective > 0 ? formatHours(effective) : "");
    }
  }, [effective, focused]);

  function commit() {
    // Untouched blur = cancel. The on-focus clear emptied the draft
    // for the user's convenience, but they walked away without
    // typing — don't delete any existing override behind their back.
    if (!touched) {
      setDraft(effective > 0 ? formatHours(effective) : "");
      setFocused(false);
      return;
    }
    const trimmed = draft.trim();
    if (trimmed === "") {
      if (isOverride) deleteMut.mutate();
      setFocused(false);
      setTouched(false);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0 || n > 240) {
      // Snap the display back to the stored value on an invalid
      // entry — never persist garbage.
      setDraft(effective > 0 ? formatHours(effective) : "");
      setFocused(false);
      setTouched(false);
      return;
    }
    const rounded = Math.round(n * 100) / 100;
    if (bucket === "work" && Math.abs(rounded - computed) < 0.001) {
      if (isOverride) deleteMut.mutate();
    } else if (!isOverride || Math.abs(rounded - (override ?? 0)) >= 0.001) {
      upsertMut.mutate(rounded);
    }
    setFocused(false);
    setTouched(false);
  }

  const tdCls =
    "p-0 h-11 "
    + (isWeekend ? "bg-slate-50/60 dark:bg-slate-800/60 " : "")
    + (border ? "border-l border-slate-200 dark:border-slate-700 " : "");

  // Always render an input, styled to look like plain text when
  // not focused — swapping between a text node and an <input>
  // between renders is what caused the row to twitch. Idle: no
  // border, no bg (parent td's weekend tint shows through), text
  // weight / color driven by override/zero state. Focus: indigo
  // ring + wash, no layout change.
  const idleColor = isOverride
    ? "text-indigo-700 dark:text-indigo-300 font-semibold"
    : effective === 0
      ? "text-slate-300 dark:text-slate-600"
      : "text-slate-800 dark:text-slate-100";

  return (
    <td className={tdCls}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        readOnly={!mayEdit}
        value={draft}
        placeholder={effective > 0 ? "" : "–"}
        onFocus={() => {
          if (!mayEdit) return;
          setFocused(true);
          setTouched(false);
          // Clear the field entirely on focus so the supervisor can
          // just type the new number without having to delete the
          // existing one first. If they blur without typing, commit()
          // sees !touched and restores the previous value rather
          // than deleting the override.
          setDraft("");
        }}
        onChange={(e) => { setTouched(true); setDraft(e.target.value); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === "Escape") {
            setDraft(effective > 0 ? formatHours(effective) : "");
            e.currentTarget.blur();
          }
        }}
        style={{ boxSizing: "border-box" }}
        className={
          "block w-full h-full text-center tabular-nums bg-transparent border-0 outline-none m-0 p-0 leading-none "
          + idleColor + " "
          + (mayEdit
            ? "cursor-pointer focus:cursor-text focus:bg-indigo-50 dark:focus:bg-indigo-500/20 focus:ring-1 focus:ring-indigo-400 dark:focus:ring-indigo-500 focus:rounded hover:bg-indigo-50/60 dark:hover:bg-indigo-500/10 "
            : "cursor-default ")
        }
        title={
          isOverride
            ? `Override: ${effective}h (schedule: ${formatHours(computed)}). Edit to change; clear to reset.`
            : mayEdit
              ? "Click to override for payroll"
              : undefined
        }
      />
    </td>
  );
}

// ---------------------------------------------------------------
// Daily hours computation — unchanged from the earlier Records
// implementation. Mirrors what the schedule cell shows for an
// (employee, date):
//   1. Whole-day approved time off → 0.
//   2. Base assignment: parsed custom-hour note, else the shift's
//      default duration (unless OT fully replaces it).
//   3. No base assignment → effective home shift hours (unless OT
//      alone meets/exceeds it).
//   4. OT rows added on top (ot_start_time / ot_end_time).
//   5. Partial-day approved time off subtracted.
// Clamped at 0.

function isOtRow(a: Assignment): boolean {
  const n = (a.note ?? "").trim().toUpperCase();
  return n === "OT" || n === "OT½" || n === "OT/2";
}

function rangeHours(start: number, end: number): number {
  const raw = end - start;
  return raw > 0 ? raw : raw + 24;
}

function hoursBetweenHHMM(start: string, end: string): number {
  const sh = hourOf(start);
  const eh = hourOf(end);
  if (sh === null || eh === null) return 0;
  const parseMin = (s: string): number => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(s);
    return m ? Number(m[1]) * 60 + Number(m[2]) : sh * 60;
  };
  const sm = parseMin(start);
  const em = parseMin(end);
  const raw = (em - sm) / 60;
  return raw > 0 ? raw : raw + 24;
}

function computeDailyHours(args: {
  employee: Employee;
  date: string;
  agency: Agency | null;
  assignmentsByKey: Map<string, Assignment[]>;
  otGroupSize: Map<string, number>;
  timeOffByKey: Map<string, TimeOff[]>;
  shiftById: Map<string, Shift>;
  codeMap: Map<string, ShiftCode>;
}): number {
  const { employee, date, agency, assignmentsByKey, otGroupSize,
    timeOffByKey, shiftById, codeMap } = args;
  const key = `${employee.id}|${date}`;
  const timeOffs = timeOffByKey.get(key) ?? [];
  if (timeOffs.some((t) => !t.startTime && !t.endTime)) return 0;

  const assns = assignmentsByKey.get(key) ?? [];
  const baseAssn = assns.find((a) => !isOtRow(a));
  const otAssns = assns.filter(isOtRow);
  const otHours = otAssns.reduce((sum, a) => {
    return a.otStartTime && a.otEndTime
      ? sum + hoursBetweenHHMM(a.otStartTime, a.otEndTime)
      : sum;
  }, 0);
  const effHomeShiftId = effectiveHomeShiftFor(agency, employee, date);
  const homeShift = effHomeShiftId ? shiftById.get(effHomeShiftId) : null;
  const homeHours = homeShift ? homeShift.durationMin / 60 : 0;
  const otReplacesHome = otAssns.length > 0 && otHours >= homeHours;
  void otGroupSize;

  let hours = 0;
  if (baseAssn) {
    const note = (baseAssn.note ?? "").trim().toUpperCase();
    const code = codeMap.get(note);
    if (code && code.coverageValue === 0) {
      // Off code — 0 base hours.
    } else if (otReplacesHome) {
      // OT fully replaces home — skip the base contribution.
    } else {
      const parsed = parseHourNote(note);
      if (parsed) {
        hours += rangeHours(parsed[0], parsed[1]);
      } else {
        const s = shiftById.get(baseAssn.shiftId);
        if (s) hours += s.durationMin / 60;
      }
    }
  } else if (!otReplacesHome) {
    if (homeShift) hours += homeShift.durationMin / 60;
  }

  for (const a of otAssns) {
    if (a.otStartTime && a.otEndTime) {
      hours += hoursBetweenHHMM(a.otStartTime, a.otEndTime);
    }
  }

  for (const t of timeOffs) {
    if (t.startTime && t.endTime) {
      hours -= hoursBetweenHHMM(t.startTime, t.endTime);
    }
  }

  return Math.max(0, hours);
}

function formatHours(h: number): string {
  if (h <= 0) return "–";
  const rounded = Math.round(h * 2) / 2;
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
}
